import { useState, useEffect } from "react";
import API from "@/lib/api";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import WeeklyReport from "@/components/WeeklyReport";
import SchoolAdmins from "@/components/SchoolAdmins";
import {
  Users, Settings, TrendingUp, ChevronRight, ChevronLeft,
  Plus, Copy, Trash2, UserMinus, BookOpen, ShieldCheck,
  Trophy, Crown, Medal, AlertTriangle, Eye,
} from "lucide-react";

const SKILL_NAMES = ["Add to 18", "Sub from 18", "Mult to 144", "Div from 144"];

const SKILL_FULL_NAMES = [
  "Addition Facts to 18", "Subtraction Facts from 18",
  "Multiplication Facts to 144", "Division Facts from 144",
];

export default function TeacherDashboard() {
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [settings, setSettings] = useState({ miss_threshold: 5, bonus_mode_enabled: true, weekly_goal: 20 });
  const [reportKey, setReportKey] = useState(0);
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentDetail, setStudentDetail] = useState(null);
  const [classLeaderboard, setClassLeaderboard] = useState([]);
  const [struggling, setStruggling] = useState([]);
  const [newClassName, setNewClassName] = useState("");
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewBand, setPreviewBand] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [classRes, studRes, setRes, strugRes] = await Promise.all([
        API.get("/teacher/classes"),
        API.get("/teacher/students"),
        API.get("/teacher/settings"),
        API.get("/teacher/struggling"),
      ]);
      setClasses(classRes.data.classes);
      setStudents(studRes.data.students);
      setSettings(setRes.data);
      setStruggling(strugRes.data.struggling);
    } catch (err) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const loadClassLeaderboard = async (classId) => {
    try {
      const { data } = await API.get(`/teacher/classes/${classId}/leaderboard`);
      setClassLeaderboard(data.leaderboard);
    } catch {
      setClassLeaderboard([]);
    }
  };

  const selectClass = (classId) => {
    if (selectedClass === classId) {
      setSelectedClass(null);
      setClassLeaderboard([]);
    } else {
      setSelectedClass(classId);
      loadClassLeaderboard(classId);
    }
  };

  const createClass = async () => {
    if (!newClassName.trim()) return;
    setCreating(true);
    try {
      const { data } = await API.post("/teacher/classes", { name: newClassName.trim() });
      setClasses((prev) => [...prev, { ...data, student_count: 0 }]);
      setNewClassName("");
      toast.success(`Class "${data.name}" created — code: ${data.code}`);
    } catch (err) {
      toast.error("Failed to create class");
    } finally {
      setCreating(false);
    }
  };

  const deleteClass = async (classId) => {
    if (!window.confirm("Delete this class? Students will be unassigned.")) return;
    try {
      await API.delete(`/teacher/classes/${classId}`);
      setClasses((prev) => prev.filter((c) => c.id !== classId));
      if (selectedClass === classId) { setSelectedClass(null); setClassLeaderboard([]); }
      toast.success("Class deleted");
      loadData();
    } catch (err) {
      toast.error("Failed to delete class");
    }
  };

  const removeStudent = async (classId, studentId) => {
    if (!window.confirm("Remove this student from the class?")) return;
    try {
      await API.delete(`/teacher/classes/${classId}/students/${studentId}`);
      toast.success("Student removed");
      loadData();
    } catch (err) {
      toast.error("Failed to remove student");
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success("Class code copied!");
  };

  const loadPreview = async (bandId) => {
    if (previewBand === bandId) { setPreviewBand(null); setPreviewData(null); return; }
    setPreviewBand(bandId);
    setPreviewLoading(true);
    try {
      const { data } = await API.get(`/skill-bands/preview/${bandId}`);
      setPreviewData(data);
    } catch { setPreviewData(null); }
    finally { setPreviewLoading(false); }
  };

  const refreshPreview = async () => {
    if (previewBand === null) return;
    setPreviewLoading(true);
    try {
      const { data } = await API.get(`/skill-bands/preview/${previewBand}`);
      setPreviewData(data);
    } catch { /* ignore */ }
    finally { setPreviewLoading(false); }
  };

  const changeRole = async (studentId, currentRole) => {
    const newRole = currentRole === "student" ? "teacher" : "student";
    if (!window.confirm(`Change this user's role to ${newRole}?`)) return;
    try {
      await API.put(`/teacher/students/${studentId}/role`, { role: newRole });
      toast.success(`Role changed to ${newRole}`);
      loadData();
    } catch (err) {
      toast.error("Failed to change role");
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await API.put("/teacher/settings", settings);
      toast.success("Settings saved");
      setReportKey((k) => k + 1);
    } catch (err) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const viewStudent = async (id) => {
    try {
      const { data } = await API.get(`/teacher/student/${id}`);
      setStudentDetail(data);
      setSelectedStudent(id);
    } catch (err) {
      toast.error("Failed to load student");
    }
  };

  const filteredStudents = selectedClass
    ? students.filter((s) => s.class_id === selectedClass)
    : students;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="font-mono text-cyber animate-pulse tracking-widest">LOADING CONTROL CENTER...</div>
      </div>
    );
  }

  // Student detail view
  if (selectedStudent && studentDetail) {
    return (
      <div className="min-h-screen p-6 lg:p-10 max-w-6xl mx-auto" data-testid="student-detail-view">
        <button
          onClick={() => { setSelectedStudent(null); setStudentDetail(null); }}
          className="flex items-center gap-2 text-white/40 hover:text-white mb-6 font-body text-sm transition-colors duration-200"
          data-testid="back-to-students"
        >
          <ChevronLeft className="w-4 h-4" /> Back to All Students
        </button>
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold text-white tracking-tight">{studentDetail.name}</h1>
          <p className="font-body text-white/40">{studentDetail.email}</p>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <MiniStat label="Accuracy" value={`${studentDetail.accuracy}%`} />
          <MiniStat label="Skill Band" value={(studentDetail.current_skill_band || 0) + 1} />
          <MiniStat label="Total Attempts" value={studentDetail.total_attempts} />
          <MiniStat label="Ship Level" value={(studentDetail.ship_level || 0) + 1} />
        </div>
        {studentDetail.recent_attempts?.length > 0 && (
          <div className="mb-8">
            <h2 className="font-heading text-lg font-bold text-white mb-4">Recent Attempts</h2>
            <div className="glass-surface rounded-xl overflow-hidden max-h-80 overflow-y-auto">
              <table className="w-full">
                <thead className="sticky top-0 bg-surface">
                  <tr className="border-b border-white/5">
                    <th className="text-left p-3 font-mono text-xs text-white/40 uppercase">Problem</th>
                    <th className="text-left p-3 font-mono text-xs text-white/40 uppercase">Answer</th>
                    <th className="text-left p-3 font-mono text-xs text-white/40 uppercase">Correct</th>
                    <th className="text-left p-3 font-mono text-xs text-white/40 uppercase">Result</th>
                  </tr>
                </thead>
                <tbody>
                  {studentDetail.recent_attempts.slice(0, 30).map((a, i) => (
                    <tr key={a._id || i} className="border-b border-white/5 last:border-0">
                      <td className="p-3 font-mono text-sm text-white">{a.problem_text}</td>
                      <td className="p-3 font-mono text-sm text-white/60">{a.student_answer ?? "—"}</td>
                      <td className="p-3 font-mono text-sm text-white/60">{a.correct_answer}</td>
                      <td className="p-3">
                        <span className={`font-mono text-xs px-2 py-0.5 rounded ${a.is_correct ? "bg-cyber/10 text-cyber" : a.is_timeout ? "bg-yellow-500/10 text-yellow-500" : "bg-magenta/10 text-magenta"}`}>
                          {a.is_correct ? "CORRECT" : a.is_timeout ? "TIMEOUT" : "WRONG"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {studentDetail.recent_games?.length > 0 && (
          <div>
            <h2 className="font-heading text-lg font-bold text-white mb-4">Game History</h2>
            <div className="glass-surface rounded-xl overflow-hidden">
              <table className="w-full">
                <thead><tr className="border-b border-white/5">
                  <th className="text-left p-3 font-mono text-xs text-white/40 uppercase">Mode</th>
                  <th className="text-left p-3 font-mono text-xs text-white/40 uppercase">Score</th>
                  <th className="text-left p-3 font-mono text-xs text-white/40 uppercase">Duration</th>
                </tr></thead>
                <tbody>
                  {studentDetail.recent_games.map((g, i) => (
                    <tr key={g._id || i} className="border-b border-white/5 last:border-0">
                      <td className="p-3 font-mono text-sm text-white">{g.mode?.toUpperCase()}</td>
                      <td className="p-3 font-mono text-sm text-cyber">{g.score}</td>
                      <td className="p-3 font-mono text-sm text-white/50">{Math.round(g.duration || 0)}s</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6 lg:p-10 max-w-6xl mx-auto" data-testid="teacher-dashboard">
      <div className="mb-10">
        <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white tracking-tighter mb-2">
          Control <span className="neon-text-neon">Center</span>
        </h1>
        <p className="font-body text-white/40">Manage classes, settings, and monitor student progress</p>
      </div>

      {/* Row 1: Classes + Settings */}
      <div className="grid lg:grid-cols-3 gap-6 mb-6 items-start">
        {/* Classes Panel */}
        <div className="lg:col-span-2 glass-surface rounded-xl p-6" data-testid="classes-panel">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-cyber" />
              <h2 className="font-heading text-lg font-bold text-white">Your Classes</h2>
            </div>
          </div>
          <div className="flex items-center gap-3 mb-5">
            <Input
              value={newClassName}
              onChange={(e) => setNewClassName(e.target.value)}
              placeholder="New class name (e.g. 3rd Period Math)"
              data-testid="new-class-name-input"
              className="flex-1 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-cyber focus:ring-cyber/30 font-body"
              onKeyDown={(e) => e.key === "Enter" && createClass()}
            />
            <button onClick={createClass} disabled={creating || !newClassName.trim()} data-testid="create-class-btn"
              className="btn-cyber px-4 py-2 rounded-lg font-heading text-sm tracking-wider flex items-center gap-2 shrink-0 disabled:opacity-40">
              <Plus className="w-4 h-4" />{creating ? "..." : "Create"}
            </button>
          </div>
          {classes.length === 0 ? (
            <div className="text-center py-8"><p className="font-body text-white/30">No classes yet. Create one above to get a class code.</p></div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3">
              {classes.map((c) => (
                <div key={c.id} data-testid={`class-card-${c.id}`} onClick={() => selectClass(c.id)}
                  className={`p-4 rounded-lg border cursor-pointer transition-all duration-200 ${selectedClass === c.id ? "border-cyber bg-cyber/5" : "border-white/10 hover:border-white/20"}`}>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-body font-medium text-white text-sm">{c.name}</h3>
                    <div className="flex items-center gap-1">
                      <button onClick={(e) => { e.stopPropagation(); copyCode(c.code); }} data-testid={`copy-code-${c.id}`}
                        className="p-1 text-white/30 hover:text-cyber transition-colors duration-200" title="Copy code"><Copy className="w-3.5 h-3.5" /></button>
                      <button onClick={(e) => { e.stopPropagation(); deleteClass(c.id); }} data-testid={`delete-class-${c.id}`}
                        className="p-1 text-white/30 hover:text-magenta transition-colors duration-200" title="Delete class"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-lg font-bold text-cyber tracking-wider" data-testid={`class-code-${c.id}`}>{c.code}</span>
                    <span className="font-mono text-xs text-white/30">{c.student_count} student{c.student_count !== 1 ? "s" : ""}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Settings */}
        <div className="glass-surface rounded-xl p-6" data-testid="settings-panel">
          <div className="flex items-center gap-2 mb-6">
            <Settings className="w-5 h-5 text-neon" />
            <h2 className="font-heading text-lg font-bold text-white">Settings</h2>
          </div>
          <div className="space-y-6">
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-white/70 font-body text-sm">Miss Threshold</Label>
                <span className="font-mono text-sm text-cyber" data-testid="miss-threshold-value">{settings.miss_threshold}</span>
              </div>
              <Slider value={[settings.miss_threshold]} onValueChange={([v]) => setSettings((s) => ({ ...s, miss_threshold: v }))}
                min={2} max={15} step={1} data-testid="miss-threshold-slider" className="mt-2" />
              <p className="font-body text-xs text-white/30 mt-2">Remediation after this many misses</p>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-white/70 font-body text-sm">Weekly Goal (new facts)</Label>
                <span className="font-mono text-sm text-cyber" data-testid="weekly-goal-value">{settings.weekly_goal ?? 20}</span>
              </div>
              <Slider value={[settings.weekly_goal ?? 20]} onValueChange={([v]) => setSettings((s) => ({ ...s, weekly_goal: v }))}
                min={5} max={50} step={1} data-testid="weekly-goal-slider" className="mt-2" />
              <p className="font-body text-xs text-white/30 mt-2">Shown to students on their dashboard</p>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-white/70 font-body text-sm block">Bonus Mode</Label>
                <p className="font-body text-xs text-white/30 mt-0.5">Allow 20-streak bonus mode</p>
              </div>
              <Switch checked={settings.bonus_mode_enabled} onCheckedChange={(v) => setSettings((s) => ({ ...s, bonus_mode_enabled: v }))}
                data-testid="bonus-mode-switch" />
            </div>
            <button onClick={saveSettings} disabled={saving} data-testid="save-settings-btn"
              className="w-full btn-cyber py-2.5 rounded-lg font-heading text-sm tracking-wider">
              {saving ? "SAVING..." : "SAVE SETTINGS"}
            </button>
          </div>
          <div className="mt-6 pt-6 border-t border-white/5">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-4 h-4 text-cyber" />
              <h3 className="font-body text-sm font-medium text-white">Overview</h3>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <MiniStat label="Students" value={students.length} />
              <MiniStat label="Avg Accuracy" value={`${students.length > 0 ? Math.round(students.reduce((a, s) => a + s.accuracy, 0) / students.length) : 0}%`} />
            </div>
          </div>
        </div>
      </div>

      <WeeklyReport classId={selectedClass} reloadKey={reportKey} />

      <SchoolAdmins />

      {/* Row 2: Class Leaderboard + Struggling Students */}
      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        {/* Class Leaderboard */}
        <div className="glass-surface rounded-xl p-6" data-testid="class-leaderboard">
          <div className="flex items-center gap-2 mb-5">
            <Trophy className="w-5 h-5 text-cyber" />
            <h2 className="font-heading text-lg font-bold text-white">
              {selectedClass ? `${classes.find(c => c.id === selectedClass)?.name || "Class"} Leaderboard` : "Class Leaderboard"}
            </h2>
          </div>
          {!selectedClass ? (
            <p className="font-body text-sm text-white/30 py-4">Select a class above to view its leaderboard</p>
          ) : classLeaderboard.length === 0 ? (
            <p className="font-body text-sm text-white/30 py-4">No game scores yet for this class</p>
          ) : (
            <div className="space-y-2">
              {classLeaderboard.map((entry, i) => (
                <div key={entry.id} data-testid={`lb-entry-${i}`}
                  className="flex items-center gap-3 p-3 rounded-lg bg-white/[0.02] hover:bg-white/[0.04] transition-colors duration-200">
                  {i === 0 ? <Crown className="w-5 h-5 text-yellow-400" /> :
                   i === 1 ? <Medal className="w-5 h-5 text-gray-300" /> :
                   i === 2 ? <Medal className="w-5 h-5 text-amber-600" /> :
                   <span className="font-mono text-sm text-white/30 w-5 text-center">{i + 1}</span>}
                  <div className="flex-1 min-w-0">
                    <p className="font-body text-sm text-white truncate">{entry.student_name}</p>
                  </div>
                  <span className={`font-mono text-xs px-1.5 py-0.5 rounded ${entry.mode === "bonus" ? "bg-cyber/10 text-cyber" : "bg-white/5 text-white/30"}`}>
                    {entry.mode === "bonus" ? "BONUS" : "STD"}
                  </span>
                  <span className="font-mono text-lg font-bold text-white tabular-nums w-20 text-right">{entry.score.toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Struggling Students */}
        <div className="glass-surface rounded-xl p-6" data-testid="struggling-panel">
          <div className="flex items-center gap-2 mb-5">
            <AlertTriangle className="w-5 h-5 text-magenta" />
            <h2 className="font-heading text-lg font-bold text-white">Needs Attention</h2>
            {struggling.length > 0 && (
              <span className="font-mono text-xs bg-magenta/10 text-magenta px-2 py-0.5 rounded-full">{struggling.length}</span>
            )}
          </div>
          {struggling.length === 0 ? (
            <p className="font-body text-sm text-white/30 py-4">No students currently struggling. Great job!</p>
          ) : (
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {struggling.map((s) => (
                <div key={s.id} data-testid={`struggling-${s.id}`}
                  className="p-3 rounded-lg bg-magenta/5 border border-magenta/10 hover:border-magenta/20 transition-colors duration-200">
                  <div className="flex items-center justify-between mb-1">
                    <p className="font-body text-sm text-white font-medium">{s.name}</p>
                    <div className="flex items-center gap-2">
                      {s.in_remediation && (
                        <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-magenta/20 text-magenta uppercase tracking-wider">Remediation</span>
                      )}
                      <button onClick={() => viewStudent(s.id)} className="text-xs text-cyber hover:text-white font-body transition-colors duration-200">
                        View
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="font-mono text-xs text-white/40">
                      Band {s.skill_band + 1}: {s.skill_name}
                    </span>
                    <span className={`font-mono text-xs font-bold ${s.recent_accuracy < 40 ? "text-magenta" : "text-yellow-500"}`}>
                      {s.recent_accuracy}% accuracy
                    </span>
                    <span className="font-mono text-xs text-white/30">
                      {s.attempts_at_band} attempts
                    </span>
                  </div>
                  {s.class_name && (
                    <p className="font-mono text-[10px] text-white/20 mt-1">{s.class_name}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Level Browser */}
      <div className="glass-surface rounded-xl p-6 mb-6" data-testid="level-browser">
        <div className="flex items-center gap-2 mb-5">
          <Eye className="w-5 h-5 text-cyber" />
          <h2 className="font-heading text-lg font-bold text-white">Level Browser</h2>
          <span className="font-mono text-xs text-white/30 ml-1">Preview problems at each skill band</span>
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          {SKILL_NAMES.map((name, i) => (
            <button
              key={i}
              onClick={() => loadPreview(i)}
              data-testid={`preview-band-${i}`}
              className={`px-3 py-1.5 rounded-lg font-mono text-xs transition-all duration-200 ${
                previewBand === i
                  ? "bg-cyber text-void font-bold"
                  : "bg-white/5 text-white/50 hover:bg-white/10 hover:text-white"
              }`}
            >
              {i + 1}: {name}
            </button>
          ))}
        </div>

        {previewBand !== null && (
          <div className="p-5 rounded-lg bg-white/[0.02] border border-white/5 animate-slide-up" data-testid="preview-content">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-heading text-base font-bold text-white">
                  Level {previewBand + 1}: {SKILL_FULL_NAMES[previewBand]}
                </h3>
                {previewData && (
                  <p className="font-mono text-xs text-white/30 mt-1">Timer: {previewData.band.timer}s per problem</p>
                )}
              </div>
              <button
                onClick={refreshPreview}
                data-testid="refresh-preview-btn"
                className="font-mono text-xs text-cyber hover:text-white transition-colors duration-200"
              >
                {previewLoading ? "Loading..." : "Refresh samples"}
              </button>
            </div>

            {previewLoading ? (
              <p className="font-mono text-sm text-white/30 animate-pulse">Loading samples...</p>
            ) : previewData?.samples ? (
              <div className="grid sm:grid-cols-3 gap-3">
                {previewData.samples.map((s, i) => (
                  <div key={i} data-testid={`preview-problem-${i}`}
                    className="p-4 rounded-lg bg-surface border border-white/5 text-center">
                    <p className="font-mono text-xl font-bold text-white mb-2">{s.text}</p>
                    <p className="font-mono text-sm text-cyber">= {s.answer}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="font-body text-sm text-white/30">Failed to load samples</p>
            )}
          </div>
        )}
      </div>

      {/* Students Table */}
      <div className="glass-surface rounded-xl overflow-hidden" data-testid="students-table">
        <div className="flex items-center justify-between p-5 border-b border-white/5">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-cyber" />
            <h2 className="font-heading text-lg font-bold text-white">Students</h2>
            <span className="font-mono text-xs text-white/30 ml-2">{filteredStudents.length} shown</span>
          </div>
          {selectedClass && (
            <button onClick={() => { setSelectedClass(null); setClassLeaderboard([]); }}
              className="font-body text-xs text-cyber hover:text-white transition-colors duration-200" data-testid="show-all-students">
              Show all classes
            </button>
          )}
        </div>
        {filteredStudents.length === 0 ? (
          <div className="p-10 text-center">
            <p className="font-body text-white/30">
              {classes.length === 0 ? "Create a class and share the code with students to get started"
                : selectedClass ? "No students in this class yet" : "No students enrolled yet"}
            </p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/5">
                <th className="text-left p-4 font-mono text-xs text-white/40 uppercase tracking-wider">Name</th>
                <th className="text-left p-4 font-mono text-xs text-white/40 uppercase tracking-wider">Skill Band</th>
                <th className="text-left p-4 font-mono text-xs text-white/40 uppercase tracking-wider">Accuracy</th>
                <th className="text-left p-4 font-mono text-xs text-white/40 uppercase tracking-wider">Attempts</th>
                <th className="text-left p-4 font-mono text-xs text-white/40 uppercase tracking-wider">Games</th>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {filteredStudents.map((s) => (
                <tr key={s.id} className="border-b border-white/5 last:border-0 hover:bg-white/[0.02] transition-colors duration-150">
                  <td className="p-4">
                    <p className="font-body text-sm text-white">{s.name}</p>
                    <p className="font-body text-xs text-white/30">{s.email}</p>
                  </td>
                  <td className="p-4">
                    <span className="font-mono text-sm text-white">{(s.current_skill_band || 0) + 1}</span>
                    <span className="font-body text-xs text-white/30 ml-1">{SKILL_NAMES[s.current_skill_band]}</span>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <Progress value={s.accuracy} className="h-1.5 w-16 bg-white/5" />
                      <span className="font-mono text-sm text-white/60">{s.accuracy}%</span>
                    </div>
                  </td>
                  <td className="p-4 font-mono text-sm text-white/60">{s.total_attempts}</td>
                  <td className="p-4 font-mono text-sm text-white/60">{s.games_played}</td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <button onClick={() => viewStudent(s.id)} data-testid={`view-student-${s.id}`}
                        className="text-xs text-cyber hover:text-white font-body transition-colors duration-200 flex items-center gap-1">
                        Details <ChevronRight className="w-3 h-3" />
                      </button>
                      <button onClick={() => changeRole(s.id, "student")} data-testid={`promote-student-${s.id}`}
                        className="p-1 text-white/20 hover:text-neon transition-colors duration-200" title="Promote to teacher">
                        <ShieldCheck className="w-3.5 h-3.5" />
                      </button>
                      {s.class_id && (
                        <button onClick={() => removeStudent(s.class_id, s.id)} data-testid={`remove-student-${s.id}`}
                          className="p-1 text-white/20 hover:text-magenta transition-colors duration-200" title="Remove from class">
                          <UserMinus className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="p-3 rounded-lg bg-white/[0.02] border border-white/5">
      <p className="font-mono text-xl font-bold text-white">{value}</p>
      <p className="font-body text-xs text-white/40 mt-1">{label}</p>
    </div>
  );
}
